// AC21: Priority border-accent colors (left-border on each card)
// AC25: Status 'new' maps to display label 'Pending Action' on institute-side cards

export interface BadgeStyle {
    bg: string;
    text: string;
}

export interface PriorityStyle extends BadgeStyle {
    border: string;      // left-border accent color on the card
    badgeBorder: string; // border-color for the priority badge chip
}

const PRIORITY_STYLES: Record<string, PriorityStyle> = {
    critical: { border: '#dc2626', bg: '#fef2f2', text: '#dc2626', badgeBorder: '#fecaca' },
    high:     { border: '#f97316', bg: '#fff7ed', text: '#c2410c', badgeBorder: '#fed7aa' },
    medium:   { border: '#d97706', bg: '#fef3c7', text: '#b45309', badgeBorder: '#fde68a' },
    low:      { border: '#6b7280', bg: '#dcfce7', text: '#16a34a', badgeBorder: '#bbf7d0' },
};

// AC25: status 'new' → label 'Pending Action' for institute-admin view
// Colors match internal-admin HTML prototype; institute-admin card uses its own local styles (INST_STATUS)
const STATUS_STYLES: Record<string, { label: string } & BadgeStyle> = {
    new:         { label: 'Pending Action', bg: '#dbeafe', text: '#1d4ed8' },
    underReview: { label: 'Under Review',   bg: '#ede9fe', text: '#7c3aed' },
    acknowledged:{ label: 'Acknowledged',   bg: '#dbeafe', text: '#1d4ed8' },
    inprogress:  { label: 'In Progress',    bg: '#fef3c7', text: '#b45309' },
    resolved:    { label: 'Resolved',       bg: '#dcfce7', text: '#16a34a' },
    discarded:   { label: 'Discarded',      bg: '#e5e7eb', text: '#6b7280' },
    closed:      { label: 'Closed',         bg: '#e5e7eb', text: '#6b7280' },
    // System log statuses — not user-selectable, shown read-only in timeline
    grievance_push:     { label: 'Pushed to Institute', bg: '#e0e7ff', text: '#4338ca' },
    grievance_push_tag: { label: 'Tag Added',           bg: '#e0e7ff', text: '#4338ca' },
};

export const getPriorityStyle = (priority: string): PriorityStyle =>
    PRIORITY_STYLES[priority?.toLowerCase()] ?? PRIORITY_STYLES['low'];

export const getStatusStyle = (status: string): { label: string } & BadgeStyle =>
    STATUS_STYLES[status] ?? { label: status, bg: '#e5e7eb', text: '#6b7280' };

export const getPriorityLabel = (priority: string): string => {
    const map: Record<string, string> = {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
    };
    return map[priority?.toLowerCase()] ?? priority;
};

// AC24: Canonical order for Status Update modal dropdown (6 admin-selectable values)
export const V2_STATUS_OPTIONS = [
    { value: 'new',          label: 'New' },
    { value: 'underReview',  label: 'Under Review' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'inprogress',   label: 'In Progress' },
    { value: 'resolved',     label: 'Resolved' },
    { value: 'discarded',    label: 'Discarded' },
] as const;
